import React from 'react';
import PropTypes from 'prop-types';

import Layout from '../../components/Core/Layout';
import SEO from '../../components/Core/SEO';
import Redirect from '../../components/Core/Redirect';
//
import { camelToKebabCase } from '../../utils';
//
import Hero from '../../components/Prodigy/Shared/Hero';
import HeroBanner from '../../components/Prodigy/Shared/HeroBanner';
import Questions from '../../components/Prodigy/Admin/Questions';
import VideoBanner from '../../components/Prodigy/Shared/VideoBanner';
import Features from '../../components/Prodigy/Admin/Features';
import Awards from '../../components/Prodigy/Shared/Awards';
import AdminTestimonials from '../../components/Prodigy/Admin/AdminTestimonials';
import PremiumBanner from '../../components/Prodigy/Shared/PremiumBanner';
import PrefineryBanner from '../../components/Prodigy/Shared/PrefineryBanner';
import BlogBannerCards from '../../components/Prodigy/Shared/BlogBannerCards';
import GraphicInfoSection from '../../components/Prodigy/Shared/GraphicInfoSection';
import DataSection from '../../components/Prodigy/Admin/DataSection';
import DualColorBlock from '../../components/Prodigy/Admin/DualColorBlock';
//
import CareersSection from '../../components/Prodigy/Careers/CareersSection';
import LocationSection from '../../components/Prodigy/Careers/LocationSection';
import ValuesSection from '../../components/Prodigy/Careers/ValuesSection';
import PetsSection from '../../components/Prodigy/Shared/PetsSection';
import EpisodeSection from '../../components/Prodigy/Shared/EpisodeSection';
import CaseStudySection from '../../components/Prodigy/Shared/CaseStudySection';
import VideoPlayer from '../../components/Prodigy/Shared/VideoPlayer';
import VideoPlayerWithCTA from '../../components/Prodigy/Shared/VideoPlayerWithCTA';
import FAQs from '../../components/Prodigy/Shared/FAQs';
import AcademicAchievement from '../../components/Prodigy/Shared/AcademicAchievement';
import PricingSection from '../../components/Prodigy/Shared/PricingSection';
import TutoringPricingSection from '../../components/Prodigy/Tutoring/TutoringPricingSection';
import RichTextSection from '../../components/Prodigy/Shared/RichText';
//
import BlogHeading from '../../components/Prodigy/Blog/BlogHeading';
import FeaturedPost from '../../components/Prodigy/Blog/FeaturedPost';
import AllBlogPosts from '../../components/Prodigy/Blog/AllBlogPosts';
import PopularPosts from '../../components/Prodigy/Blog/PopularPosts';
import CultureAllBlogPosts from '../../components/Prodigy/Culture/CultureAllBlogPosts';
import Profile from '../../components/Prodigy/Shared/Profile';

function DefaultTemplate({ location, pageContext, canonical }) {
  if (pageContext.shouldRedirect) {
    return (
      <Redirect
        origin={location}
        location={pageContext.intl.originalPath}
        getLangPreference={!pageContext.intl.routed}
        defaultLangKey={pageContext.intl.redirectTo}
      />
    );
  }

  const {
    title,
    lang,
    id: pageId,
    theme,
    metaData: { metatitle, metaDescription, metaNoIndex },
    slices,
    blogSearchBar,
  } = pageContext.data;
  // * pass data from hero buttont text -> hubspot

  // * Lang is the html-lang of the content on the page
  // * pageContext.intl.language is the language that the page should be associated with
  // * if lang and pageContext.intl.language dont match, that means we are on a translated paged without translated copy, i.e the copy duplicates that of the main page, so we set a canonical link

  return (
    <Layout
      cmsTheme={theme}
      location={location}
      lang={pageContext.intl ? pageContext.intl.language : lang}
      pageId={pageId}
    >
      <SEO
        title={metatitle || title[0].text}
        description={metaDescription}
        noIndex={metaNoIndex}
        lang={pageContext.intl ? pageContext.intl.language : lang}
        location={location}
        canonical={pageContext.intl ? pageContext.intl.canonical : canonical}
      />
      {blogSearchBar && (
        <BlogHeading data={blogSearchBar} location={location} />
      )}

      {slices.map(({ primary, items }, index) => {
        if (!primary[0]) return null;
        primary[0] = {
          ...primary[0],
          lang: pageContext.intl ? pageContext.intl.language : lang,
        };

        const sectionId = camelToKebabCase(
          `section-${index}-${primary[0].type}`,
        );
        const key = `${primary[0].type}-${title[0].text}-${index.toString()}`;

        switch (primary[0].type) {
          case 'heroSlice':
            return <Hero data={primary[0]} key={key} />;

          case 'heroBanner':
            return <HeroBanner data={primary[0]} key={key} />;

          case 'columnImagesOverTextPrimary':
            if (
              primary[0].component === 'Data Section'
              || primary[0].component === 'Proven Results Section'
            ) {
              return (
                <DataSection
                  sectionId={sectionId}
                  data={{ ...primary[0], items }}
                  key={`${primary[0].type}-${title[0].text}-${
                    primary[0].component
                  }-${index.toString()}`}
                />
              );
            }
            if (primary[0].component === 'Opportunities Section') {
              return (
                <LocationSection
                  sectionId={sectionId}
                  data={{ ...primary[0], items }}
                  key={`${primary[0].type}-${title[0].text}-${
                    primary[0].component
                  }-${index.toString()}`}
                />
              );
            }
            if (primary[0].component === 'Who We Are Section') {
              return (
                <ValuesSection
                  sectionId={sectionId}
                  data={{ ...primary[0], items }}
                  key={`${primary[0].type}-${title[0].text}-${
                    primary[0].component
                  }-${index.toString()}`}
                />
              );
            }
            if (primary[0].component === 'Pets Section') {
              return (
                <PetsSection
                  sectionId={sectionId}
                  data={{ ...primary[0], items }}
                  key={`${primary[0].type}-${title[0].text}-${
                    primary[0].component
                  }-${index.toString()}`}
                />
              );
            }
            if (primary[0].component === 'Episode Section') {
              return (
                <EpisodeSection
                  sectionId={sectionId}
                  data={{ ...primary[0], items }}
                  key={`${primary[0].type}-${title[0].text}-${
                    primary[0].component
                  }-${index.toString()}`}
                />
              );
            }
            return (
              <Questions
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={`${primary[0].type}-${title[0].text}-${
                  primary[0].component
                }-${index.toString()}`}
              />
            );
          case 'caseStudySectionPrimary':
            return (
              <CaseStudySection
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={`${primary[0].type}-${title[0].text}-${
                  primary[0].component
                }-${index.toString()}`}
              />
            );
          case 'videoSlice':
            return (
              <VideoPlayer
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={key}
              />
            );
          case 'videoSliceWithCTA':
            return (
              <VideoPlayerWithCTA
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={key}
              />
            );
          case 'pageFilterPrimary':
            return (
              <CareersSection
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={key}
              />
            );
          case 'simpleBannerPrimary':
            return (
              <PremiumBanner
                sectionId={sectionId}
                data={primary[0]}
                key={key}
              />
            );
          case 'prefineryBannerPrimary':
            return (
              <PrefineryBanner
                sectionId={sectionId}
                data={primary[0]}
                key={key}
              />
            );
          case 'videoWithListPrimary':
            if (primary[0].component === 'Video Banner') {
              return (
                <VideoBanner
                  sectionId={sectionId}
                  data={{ ...primary[0], items }}
                  key={`${primary[0].type}-${title[0].text}-${
                    primary[0].component
                  }-${index.toString()}`}
                />
              );
            }
            return (
              <Features
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={`${primary[0].type}-${title[0].text}-${
                  primary[0].component
                }-${index.toString()}`}
              />
            );
          case 'awardPrimary':
            return (
              <Awards
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={key}
              />
            );
          case 'testimonialPrimary':
            return (
              <AdminTestimonials
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={key}
              />
            );
          case 'imageBesideTextPrimary':
            return (
              <GraphicInfoSection
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={key}
              />
            );
          case 'fullWidthImageAndTextBoxesPrimary':
            return (
              <BlogBannerCards
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={key}
              />
            );
          case 'faqPrimary':
            return (
              <FAQs
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={`faqPrimary-${index.toString()}`}
              />
            );
          case 'profilePrimary':
            return (
              <Profile
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={`profile-${index.toString()}`}
              />
            );
          case 'dualColourTextPrimary':
            return (
              <DualColorBlock
                sectionId={sectionId}
                data={primary[0]}
                key={key}
              />
            );
          case 'blogLandingFeaturedPosts':
  <FeaturedPost sectionId={sectionId} data={{ primary }} key={key} />;
            if (primary[0].component === 'Culture Landing Page') {
              return (
                <CultureAllBlogPosts
                  sectionId={sectionId}
                  key={key}
                  lang={pageContext.intl.language}
                />
              );
            }
            // 'Default Blog Post Landing Page'
            return <AllBlogPosts sectionId={sectionId} key={key} lang={lang} />;

          case 'articleGroupPrimary':
            return (
              <PopularPosts
                sectionId={sectionId}
                data={{ primary, items }}
                key={key}
              />
            );
          case 'researchPrimary':
            return (
              <AcademicAchievement
                sectionId={sectionId}
                data={{ primary }}
                key={key}
              />
            );
          case 'richTextSlice':
            return <RichTextSection data={primary[0]} key={key} />;
          case 'priceCardPrimary':
            if (primary[0].sectionStyle === 'Tutoring') {
              return (
                <TutoringPricingSection
                  sectionId={sectionId}
                  data={{ ...primary[0], items }}
                  key={`${primary[0].type}-${
                    title[0].text
                  }-${index.toString()}`}
                />
              );
            }

            return (
              <PricingSection
                sectionId={sectionId}
                data={{ ...primary[0], items }}
                key={key}
              />
            );
          default:
            return null;
        }
      })}
    </Layout>
  );
}

DefaultTemplate.propTypes = {
  location: PropTypes.shape().isRequired,
  pageContext: PropTypes.shape().isRequired,
  canonical: PropTypes.bool,
};

DefaultTemplate.defaultProps = {
  canonical: false,
};

export default DefaultTemplate;
